// How the view-as switcher names a borrowed person, and how it groups them.
//
// The banner and the aria-label both have to say the role, not just the name:
// "Viewing as Deborah" is what this issue exists to stop, because leadership
// is not a reduced strategist. Split out of the React so `node --test` can
// cover the wording without jsdom.

import type { ViewAsPerson } from '../api/types';

export const VIEW_AS_ROLE_GROUPS = [
  { role: 'leadership', label: 'Leadership' },
  { role: 'strategist', label: 'Strategists' },
  { role: 'viewer', label: 'Viewers' },
  // Account executives (GRO-360). The only group leadership is offered.
  { role: 'ae', label: 'AEs' },
] as const;

export function firstNameFromEmail(email: string): string {
  const localPart = (email || '').split('@')[0] ?? '';
  const firstToken = localPart.split('.')[0] ?? '';
  if (!firstToken) return email;
  return firstToken.slice(0, 1).toUpperCase() + firstToken.slice(1);
}

export function viewAsLine(name: string, role: string | null | undefined): string {
  if (!role) return `Viewing as ${name}`;
  // Role ids are lowercase words; "ae" is an acronym and reads as one.
  const roleLabel = role === 'ae' ? 'AE' : role;
  return `Viewing as ${name} · ${roleLabel}`;
}

/** Whether this viewer may change board pins and hides: not under a read-only view-as. */
export function canEditBoardPrefs(viewer: { acting_for?: string | null; impersonator?: string | null } | undefined): boolean {
  return !viewer?.acting_for || viewAsWritesAllowed(viewer);
}

/** True when a view-as may write as the target (an allowlisted impersonator), not read only. */
export function viewAsWritesAllowed(viewer: { impersonator?: string | null } | undefined): boolean {
  return Boolean(viewer?.impersonator);
}

/** The banner's tail: read only, or who the actions are recorded as. */
export function viewAsBannerTail(name: string, impersonator: string | null | undefined): string {
  if (!impersonator) return 'read only';
  return `you can act as ${name}; every action is recorded as ${firstNameFromEmail(impersonator)} acting as ${name}`;
}

export function viewAsSwitcherLabel(selected: ViewAsPerson | undefined): string {
  return selected
    ? `View the dashboard as ${selected.name} · ${selected.role}`
    : 'View the dashboard as another role';
}

export function groupViewAsPeople(people: ViewAsPerson[]): {
  role: string;
  label: string;
  people: ViewAsPerson[];
}[] {
  const grouped: { role: string; label: string; people: ViewAsPerson[] }[] = VIEW_AS_ROLE_GROUPS
    .map(group => ({
      role: group.role,
      label: group.label,
      people: people.filter(person => person.role === group.role),
    }))
    .filter(group => group.people.length > 0);
  const knownRoles = new Set<string>(VIEW_AS_ROLE_GROUPS.map(group => group.role));
  const leftover = people.filter(person => !knownRoles.has(person.role));
  if (leftover.length) {
    grouped.push({ role: 'other', label: 'Other', people: leftover });
  }
  return grouped;
}
